import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '../../shared/TailwindComponents/Button';
import GlassCard from '../../shared/brand/GlassCard';
import { DashboardCalendarEvent } from '../../../store/reducer/DashboardReducer';
import {
  PiCalendarLight,
  PiMapPinLight,
  PiClockLight,
} from 'react-icons/pi';

interface UpcomingEventsProps {
  events: DashboardCalendarEvent[];
}

/**
 * Event type → status token. Only the four kinds that carry real urgency get a
 * colour; `task` and `imported` used to be violet and cyan, which said nothing
 * about the event and did not flip cleanly in dark mode.
 */
const eventTypeColors: Record<string, string> = {
  hearing: 'bg-danger-100 text-danger-700 dark:bg-danger-900/30 dark:text-danger-400',
  deadline: 'bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400',
  meeting: 'bg-info-100 text-info-700 dark:bg-info-900/30 dark:text-info-400',
  task: 'bg-surface-sunken text-fg-body',
  task_deadline: 'bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400',
  matter_deadline: 'bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400',
  custom: 'bg-surface-sunken text-fg-body',
  imported: 'bg-surface-sunken text-fg-body',
};

const formatEventTime = (startTime: string | null, endTime: string | null, allDay: boolean): string => {
  if (!startTime) return '';
  if (allDay) return 'All day';

  const start = new Date(startTime);
  const now = new Date();
  const diffDays = Math.ceil((start.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  const timeStr = start.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

  if (diffDays === 0) return `Today at ${timeStr}`;
  if (diffDays === 1) return `Tomorrow at ${timeStr}`;
  if (diffDays < 7) {
    const day = start.toLocaleDateString(undefined, { weekday: 'short' });
    return `${day} at ${timeStr}`;
  }
  return start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ` at ${timeStr}`;
};

const UpcomingEvents: React.FC<UpcomingEventsProps> = ({ events }) => {
  const { t } = useTranslation();
  const history = useHistory();

  if (events.length === 0) return null;

  return (
    <GlassCard
      icon={<PiCalendarLight />}
      title={t('Upcoming Events')}
      actions={
        <Button variant="outline" size="small" onClick={() => history.push('/calendar')}>
          {t('View Calendar')}
        </Button>
      }
      padding="none"
      className="overflow-hidden"
    >
      <div className="divide-y divide-hairline-soft">
        {events.map((event) => {
          const colorClass = eventTypeColors[event.event_type] || eventTypeColors.custom;
          return (
            <div
              key={event.id}
              className="flex items-start gap-3 px-5 py-3 hover:bg-surface-sunken/70 cursor-pointer transition-colors"
              onClick={() => history.push('/calendar')}
            >
              <div className={`flex-shrink-0 mt-0.5 px-2 py-0.5 rounded text-[11px] first-letter:uppercase ${colorClass}`}>
                {event.event_type.replace(/_/g, ' ')}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900 dark:text-gray-100 truncate">
                  {event.title}
                </p>
                <div className="flex items-center gap-3 mt-0.5">
                  <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                    <PiClockLight className="w-3 h-3" />
                    {formatEventTime(event.start_time, event.end_time, event.all_day)}
                  </span>
                  {event.location && (
                    <span className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1 truncate">
                      <PiMapPinLight className="w-3 h-3" />
                      {event.location}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </GlassCard>
  );
};

export default UpcomingEvents;
